import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Target, Trophy, Award, Filter, Search, List, X, Plus, Image, List as ListIcon, Image as ImageIcon } from 'lucide-react';

interface ReadingChallenge {
  id: string;
  title: string;
  description: string;
  type: 'daily' | 'weekly' | 'monthly' | 'custom' | 'streak' | 'achievement';
  category: 'reading' | 'learning' | 'community' | 'exploration' | 'mastery';
  difficulty: 'easy' | 'medium' | 'hard' | 'expert';
  target: number;
  unit: 'articles' | 'minutes' | 'words' | 'pages' | 'days' | 'highlights' | 'comments';
  current: number;
  progress: number;
  isCompleted: boolean;
  isActive: boolean;
  startDate: Date;
  endDate?: Date;
  rewards: {
    points: number;
    badges: string[];
    achievements: string[];
    unlocks: string[];
  };
  requirements: string[];
  tips: string[];
  participants: number;
  completionRate: number;
  averageTime: number;
  isJoined: boolean;
  streak?: number;
  bestStreak?: number;
  lastCompleted?: Date;
  createdAt: Date;
  updatedAt: Date;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: 'reading' | 'community' | 'learning' | 'exploration' | 'mastery';
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  points: number;
  isUnlocked: boolean;
  unlockedAt?: Date;
  progress: number;
  target: number;
  requirements: string[];
  rewards: {
    points: number;
    badges: string[];
    unlocks: string[];
  };
}

interface ReadingChallengesProps {
  userId: string;
  onChallengeJoin?: (challengeId: string) => void;
  onChallengeLeave?: (challengeId: string) => void;
  onAchievementUnlock?: (achievement: Achievement) => void;
  className?: string;
}

const ReadingChallenges: React.FC<ReadingChallengesProps> = ({
  userId,
  onChallengeJoin,
  onChallengeLeave,
  onAchievementUnlock,
  className = ''
}) => {
  const [challenges, setChallenges] = useState<ReadingChallenge[]>([]);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [showChallenges, setShowChallenges] = useState(true);
  const [showAchievements, setShowAchievements] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'popular' | 'recent' | 'difficulty' | 'progress'>('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChallenge, setSelectedChallenge] = useState<ReadingChallenge | null>(null);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Mock data
  useEffect(() => {
    const mockChallenges: ReadingChallenge[] = [
      {
        id: '1',
        title: 'Daily Reading Streak',
        description: 'Read at least one article every day for 30 days',
        type: 'streak',
        category: 'reading',
        difficulty: 'medium',
        target: 30,
        unit: 'days',
        current: 15,
        progress: 0.5,
        isCompleted: false,
        isActive: true,
        startDate: new Date('2024-01-01'),
        endDate: new Date('2024-01-31'),
        rewards: {
          points: 500,
          badges: ['streak-master'],
          achievements: ['30-day-streak'],
          unlocks: ['golden-reader-badge']
        },
        requirements: ['Read at least 1 article per day', 'Maintain streak for 30 days'],
        tips: ['Set a daily reading reminder', 'Choose articles that interest you', 'Read during your commute'],
        participants: 1247,
        completionRate: 0.68,
        averageTime: 25,
        isJoined: true,
        streak: 15,
        bestStreak: 23,
        lastCompleted: new Date('2024-01-20'),
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-20')
      },
      {
        id: '2',
        title: 'Speed Reader Challenge',
        description: 'Read 50 articles in one week',
        type: 'weekly',
        category: 'reading',
        difficulty: 'hard',
        target: 50,
        unit: 'articles',
        current: 23,
        progress: 0.46,
        isCompleted: false,
        isActive: true,
        startDate: new Date('2024-01-15'),
        endDate: new Date('2024-01-22'),
        rewards: {
          points: 750,
          badges: ['speed-reader'],
          achievements: ['weekly-marathon'],
          unlocks: ['speed-reading-tools']
        },
        requirements: ['Read 50 articles in 7 days', 'Maintain 80% comprehension'],
        tips: ['Use speed reading techniques', 'Focus on key points', 'Take breaks between articles'],
        participants: 892,
        completionRate: 0.42,
        averageTime: 45,
        isJoined: true,
        lastCompleted: new Date('2024-01-19'),
        createdAt: new Date('2024-01-15'),
        updatedAt: new Date('2024-01-19')
      },
      {
        id: '3',
        title: 'Community Contributor',
        description: 'Make 100 highlights and 50 comments this month',
        type: 'monthly',
        category: 'community',
        difficulty: 'medium',
        target: 150,
        unit: 'highlights',
        current: 87,
        progress: 0.58,
        isCompleted: false,
        isActive: true,
        startDate: new Date('2024-01-01'),
        endDate: new Date('2024-01-31'),
        rewards: {
          points: 1000,
          badges: ['community-champion'],
          achievements: ['active-contributor'],
          unlocks: ['moderator-tools']
        },
        requirements: ['Make 100 highlights', 'Write 50 comments', 'Engage with community'],
        tips: ['Highlight key insights', 'Ask thoughtful questions', 'Respond to others'],
        participants: 2341,
        completionRate: 0.73,
        averageTime: 60,
        isJoined: false,
        lastCompleted: new Date('2024-01-18'),
        createdAt: new Date('2024-01-01'),
        updatedAt: new Date('2024-01-18')
      }
    ];

    const mockAchievements: Achievement[] = [
      {
        id: '1',
        title: 'First Steps',
        description: 'Read your first article',
        icon: '👶',
        category: 'reading',
        rarity: 'common',
        points: 10,
        isUnlocked: true,
        unlockedAt: new Date('2024-01-01'),
        progress: 1,
        target: 1,
        requirements: ['Read 1 article'],
        rewards: {
          points: 10,
          badges: ['first-reader'],
          unlocks: ['basic-highlighting']
        }
      },
      {
        id: '2',
        title: 'Speed Demon',
        description: 'Read 100 articles in a single day',
        icon: '⚡',
        category: 'reading',
        rarity: 'legendary',
        points: 1000,
        isUnlocked: false,
        progress: 23,
        target: 100,
        requirements: ['Read 100 articles in one day'],
        rewards: {
          points: 1000,
          badges: ['speed-demon'],
          unlocks: ['advanced-speed-reading']
        }
      },
      {
        id: '3',
        title: 'Community Leader',
        description: 'Get 1000 likes on your highlights',
        icon: '👑',
        category: 'community',
        rarity: 'epic',
        points: 500,
        isUnlocked: false,
        progress: 234,
        target: 1000,
        requirements: ['Get 1000 likes on highlights'],
        rewards: {
          points: 500,
          badges: ['community-leader'],
          unlocks: ['leaderboard-access']
        }
      }
    ];

    setChallenges(mockChallenges);
    setAchievements(mockAchievements);
  }, [userId]);

  // Handle challenge actions
  const handleJoinChallenge = async (challengeId: string) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setChallenges(prev => prev.map(challenge => 
      challenge.id === challengeId 
        ? { ...challenge, isJoined: true, participants: challenge.participants + 1 }
        : challenge
    ));
    
    onChallengeJoin?.(challengeId);
    setIsLoading(false);
  };

  const handleLeaveChallenge = async (challengeId: string) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setChallenges(prev => prev.map(challenge => 
      challenge.id === challengeId 
        ? { ...challenge, isJoined: false, participants: challenge.participants - 1 }
        : challenge
    ));
    
    onChallengeLeave?.(challengeId);
    setIsLoading(false);
  };

  // Filter and sort challenges
  const filteredChallenges = challenges
    .filter(challenge => {
      const matchesType = filterType === 'all' || challenge.type === filterType;
      const matchesCategory = filterCategory === 'all' || challenge.category === filterCategory;
      const matchesSearch = searchQuery === '' || 
        challenge.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        challenge.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.participants - a.participants;
        case 'recent':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'difficulty':
          const difficultyOrder = { easy: 1, medium: 2, hard: 3, expert: 4 };
          return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
        case 'progress':
          return b.progress - a.progress;
        default:
          return 0;
      }
    });

  // Get difficulty color
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-400';
      case 'hard': return 'text-orange-600 bg-orange-100 dark:bg-orange-900 dark:text-orange-400';
      case 'expert': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  // Get rarity color
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
      case 'uncommon': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-400';
      case 'rare': return 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-400';
      case 'epic': return 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-400';
      case 'legendary': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  // Format date
  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className={`reading-challenges ${className}`}>
      {/* Challenges Panel */}
      <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Reading Challenges
            </h3>
            <button
              onClick={() => setShowCreateChallenge(true)}
              className="p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          
          {/* Filters */}
          <div className="flex gap-2 mb-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">All Types</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="streak">Streak</option>
              <option value="achievement">Achievement</option>
            </select>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">All Categories</option>
              <option value="reading">Reading</option>
              <option value="learning">Learning</option>
              <option value="community">Community</option>
              <option value="exploration">Exploration</option>
              <option value="mastery">Mastery</option>
            </select>
          </div>
          
          <div className="flex gap-2 mb-4">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              <option value="popular">Popular</option>
              <option value="recent">Recent</option>
              <option value="difficulty">Difficulty</option>
              <option value="progress">Progress</option>
            </select>
            
            <button
              onClick={() => setShowChallenges(!showChallenges)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              {showChallenges ? 'Hide' : 'Show'}
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search challenges..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Challenges */}
          {showChallenges && (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <Target className="w-4 h-4" />
                Active Challenges ({filteredChallenges.length})
              </h4>
              
              {filteredChallenges.map(challenge => (
                <div
                  key={challenge.id}
                  className={`p-4 border rounded-lg transition-colors cursor-pointer ${
                    challenge.isJoined 
                      ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => setSelectedChallenge(challenge)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h5 className="font-medium text-gray-900 dark:text-white text-sm">
                      {challenge.title}
                    </h5>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyColor(challenge.difficulty)}`}>
                        {challenge.difficulty}
                      </span>
                      {challenge.isJoined && (
                        <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full">
                          Joined
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    {challenge.description}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{challenge.current}/{challenge.target} {challenge.unit}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${challenge.progress * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <span>{challenge.participants} participants</span>
                    <span>{Math.round(challenge.completionRate * 100)}% completion</span>
                    <span>{challenge.averageTime}min avg</span>
                  </div>
                  
                  {/* Rewards */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-gray-500">Rewards:</span>
                    <span className="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 rounded">
                      {challenge.rewards.points} pts
                    </span>
                    {challenge.rewards.badges.map((badge, index) => (
                      <span key={index} className="text-xs px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 rounded">
                        {badge}
                      </span>
                    ))}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {challenge.isJoined ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLeaveChallenge(challenge.id);
                        }}
                        disabled={isLoading}
                        className="px-3 py-1 bg-red-100 hover:bg-red-200 disabled:bg-gray-100 disabled:cursor-not-allowed text-red-600 rounded text-sm transition-colors"
                      >
                        Leave
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinChallenge(challenge.id);
                        }}
                        disabled={isLoading}
                        className="px-3 py-1 bg-blue-100 hover:bg-blue-200 disabled:bg-gray-100 disabled:cursor-not-allowed text-blue-600 rounded text-sm transition-colors"
                      >
                        Join
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedChallenge(challenge);
                      }}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded text-sm transition-colors"
                    >
                      Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Achievements */}
          {showAchievements && (
            <div className="mt-6">
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                <Award className="w-4 h-4" />
                Achievements
              </h4>
              
              <div className="grid grid-cols-2 gap-3">
                {achievements.map(achievement => (
                  <div
                    key={achievement.id}
                    className={`p-3 border rounded-lg transition-colors cursor-pointer ${
                      achievement.isUnlocked 
                        ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20' 
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-2">{achievement.icon}</div>
                      <h6 className="font-medium text-gray-900 dark:text-white text-xs mb-1">
                        {achievement.title}
                      </h6>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {achievement.description}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                        <span className={`px-2 py-1 rounded-full ${getRarityColor(achievement.rarity)}`}>
                          {achievement.rarity}
                        </span>
                        <span>{achievement.points} pts</span>
                      </div>
                      
                      {!achievement.isUnlocked && (
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 mb-2">
                          <div
                            className="bg-blue-500 h-1 rounded-full transition-all duration-300"
                            style={{ width: `${(achievement.progress / achievement.target) * 100}%` }}
                          />
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500">
                        {achievement.isUnlocked ? 'Unlocked!' : `${achievement.progress}/${achievement.target}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Challenge Details Modal */}
      {selectedChallenge && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {selectedChallenge.title}
              </h3>
              <button
                onClick={() => setSelectedChallenge(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Description</h4>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {selectedChallenge.description}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedChallenge.current}/{selectedChallenge.target}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Progress</div>
                </div>
                
                <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedChallenge.participants}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Participants</div>
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Requirements</h4>
                <ul className="space-y-1">
                  {selectedChallenge.requirements.map((requirement, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                      <span className="text-blue-500 mt-1">•</span>
                      <span>{requirement}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Tips</h4>
                <ul className="space-y-1">
                  {selectedChallenge.tips.map((tip, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400 flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Rewards</h4>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-400 rounded-full text-sm">
                    {selectedChallenge.rewards.points} points
                  </span>
                  {selectedChallenge.rewards.badges.map((badge, index) => (
                    <span key={index} className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400 rounded-full text-sm">
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadingChallenges;
